import React, { useMemo } from 'react';
import { useLocale } from '../LocaleProvider';

function dateOnly(value) {
  return String(value || '').slice(0, 10);
}

export function formatAssessmentDate(value, es = false) {
  const [year, month, day] = dateOnly(value).split('-');
  return year && month && day ? `${day}/${month}/${year}` : (es ? 'Fecha no informada' : 'Data não informada');
}

function numericScore(value) {
  const score = Number(value);
  return Number.isFinite(score) ? Math.max(0, Math.min(30, score)) : null;
}

export function buildChartPoints(assessments = [], width = 640, height = 260) {
  const padding = { top: 24, right: 20, bottom: 42, left: 44 };
  const usableWidth = Math.max(1, width - padding.left - padding.right);
  const usableHeight = Math.max(1, height - padding.top - padding.bottom);
  const ordered = [...assessments]
    .map((assessment, index) => ({ assessment, index }))
    .sort((left, right) => {
      const date = dateOnly(left.assessment.applied_at).localeCompare(dateOnly(right.assessment.applied_at));
      if (date !== 0) return date;
      const created = String(left.assessment.created_at || '').localeCompare(String(right.assessment.created_at || ''));
      return created !== 0 ? created : left.index - right.index;
    });

  const valid = ordered
    .map(({ assessment }) => ({
      ...assessment,
      _score: numericScore(assessment.score),
      _date: dateOnly(assessment.applied_at),
    }))
    .filter((assessment) => assessment._score !== null);

  return valid.map((assessment, index) => ({
    ...assessment,
    x: valid.length === 1
      ? padding.left + usableWidth / 2
      : padding.left + (index / (valid.length - 1)) * usableWidth,
    y: padding.top + ((30 - assessment._score) / 30) * usableHeight,
  }));
}

function dateLabel(value, es = false) {
  const formatted = formatAssessmentDate(value, es);
  return formatted === (es ? 'Fecha no informada' : 'Data não informada') ? (es ? 'sin fecha' : 'sem data') : formatted;
}

export default function PatientChart({ assessments = [], width = 640, height = 260 }) {
  const { locale } = useLocale();
  const es = locale === 'es';
  const points = useMemo(() => buildChartPoints(assessments, width, height), [assessments, width, height]);
  const line = points.map((point) => `${point.x},${point.y}`).join(' ');
  const first = points[0];
  const last = points[points.length - 1];
  const summary = points.length === 0
    ? (es ? 'Aún no hay puntuaciones válidas para mostrar.' : 'Ainda não há pontuações válidas para desenhar.')
    : points.length === 1
    ? (es ? `Una evaluación registrada el ${dateLabel(first.applied_at, es)}, con una puntuación de ${first._score} sobre 30.` : `Uma avaliação registrada em ${dateLabel(first.applied_at, es)}, com pontuação ${first._score} de 30.`)
      : (es ? `${points.length} evaluaciones, del ${dateLabel(first.applied_at, es)} al ${dateLabel(last.applied_at, es)}. La puntuación varió de ${Math.min(...points.map((point) => point._score))} a ${Math.max(...points.map((point) => point._score))}.` : `${points.length} avaliações, de ${dateLabel(first.applied_at, es)} a ${dateLabel(last.applied_at, es)}. A pontuação variou de ${Math.min(...points.map((point) => point._score))} a ${Math.max(...points.map((point) => point._score))}.`);

  const padding = { top: 24, right: 20, bottom: 42, left: 44 };
  const usableHeight = Math.max(1, height - padding.top - padding.bottom);
  const baseline = padding.top + usableHeight;
  const area = points.length > 1
    ? `${points[0].x},${baseline} ${line} ${points[points.length - 1].x},${baseline}`
    : '';

  return (
    <div className="chart-box overflow-hidden p-4" data-testid="patient-chart">
      <p className="font-display text-[length:var(--fs-17)] leading-snug text-[var(--ink)]" data-testid="patient-chart-summary">{summary}</p>
      <svg
        role="img"
        aria-labelledby="patient-chart-title patient-chart-description"
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="xMidYMid meet"
        className="mt-3 min-h-[190px] w-full"
      >
        <title id="patient-chart-title">{es ? 'Evolución de la puntuación' : 'Evolução da pontuação'}</title>
        <desc id="patient-chart-description">{summary}</desc>
        {[0, 10, 20, 30].map((score) => {
          const y = padding.top + ((30 - score) / 30) * usableHeight;
          return (
            <g key={score}>
              <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke="var(--line)" strokeDasharray="3 4" />
              <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="11" fill="var(--text-muted)">{score}</text>
            </g>
          );
        })}
        {points.length > 1 && (
          <polygon points={area} fill="var(--chart-fill)" stroke="none" />
        )}
        {points.length > 1 && (
          <polyline points={line} fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        )}
        {points.map((point) => (
          <g key={point.id || `${point._date}-${point.x}`}>
            <circle cx={point.x} cy={point.y} r="6" fill="var(--surface)" stroke="var(--accent)" strokeWidth="3">
              <title>{`${dateLabel(point.applied_at, es)}: ${point._score} de 30`}</title>
            </circle>
            <text x={point.x} y={height - 14} textAnchor="middle" fontSize="10" fill="var(--text-muted)">{dateLabel(point.applied_at, es)}</text>
          </g>
        ))}
        {!points.length && (
          <text x={width / 2} y={height / 2} textAnchor="middle" fontSize="13" fill="var(--text-muted)">{es ? 'No hay evaluaciones válidas' : 'Sem avaliações válidas'}</text>
        )}
      </svg>
      <p className="mt-1 text-xs text-[var(--text-muted)]">{es ? 'Puntuación de 0 a 30. El gráfico describe el historial; no establece un diagnóstico.' : 'Pontuação de 0 a 30. O gráfico descreve o histórico; não estabelece diagnóstico.'}</p>
    </div>
  );
}
